import TransactionHistoryChart from "../../charts/TransactionHistoryChart";
import {
  getIconSrc,
  getInitialsIconSrc,
} from "../../../../utils/images/iconUtils";
import ModernPopupBody from "../common/ModernPopupBody";
import ModernPopupHeader from "../common/ModernPopupHeader";
import ModalModern from "../ModalModern";
import { useCallback, useEffect, useRef, useState } from "react";
import { useAppToast } from "../../../../hooks/useAppToast";
import {
  getRecurringTransactionDetail,
  updateRecurringTransaction,
} from "../../../../api/finances/recurringTransactionsAPI";
import LoadingPlaceholder from "../../loading/LoadingPlaceholder";
import CategoryPill from "../../badges/CategoryPill";
import ErrorLabel from "../../forms/base/ErrorLabel";
import {
  RECURRENT_TYPE_LABELS,
  RECURRENT_TYPE_OPTIONS,
  getRecurrentTypeClassName,
  mapRecurringTransactionToRow,
} from "./utils/helpers";

function RecurringDetailsModal({
  transaction,
  onClose,
  onTransactionUpdate,
  copy,
  commonCopy,
}) {
  const [currentTransaction, setCurrentTransaction] = useState(transaction);
  const [historyData, setHistoryData] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [isSavingNote, setIsSavingNote] = useState(false);
  const [isSavingRecurrentType, setIsSavingRecurrentType] = useState(false);
  const [isRecurrentTypeMenuOpen, setIsRecurrentTypeMenuOpen] = useState(false);
  const [noteValue, setNoteValue] = useState(transaction?.note || "");
  const [updateError, setUpdateError] = useState("");
  const { toastMessage, showToast } = useAppToast();
  const recurrentTypeFieldRef = useRef(null);

  useEffect(() => {
    setCurrentTransaction(transaction);
    setNoteValue(transaction?.note || "");
    setUpdateError("");
    setIsRecurrentTypeMenuOpen(false);
  }, [transaction]);

  useEffect(() => {
    if (!isRecurrentTypeMenuOpen) return;

    const handlePointerDown = (event) => {
      if (
        recurrentTypeFieldRef.current &&
        !recurrentTypeFieldRef.current.contains(event.target)
      ) {
        setIsRecurrentTypeMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [isRecurrentTypeMenuOpen]);

  const fetchHistory = useCallback(async () => {
    if (!transaction?.recurring_uuid) return;

    setIsLoadingHistory(true);
    try {
      const response = await getRecurringTransactionDetail(
        transaction.recurring_uuid,
      );

      const rawHistory = response.history || [];
      const recurringTransaction = response.recurring_transaction;

      if (recurringTransaction) {
        const mappedTransaction =
          mapRecurringTransactionToRow(recurringTransaction);
        setCurrentTransaction(mappedTransaction);
        setNoteValue(mappedTransaction.note || "");
      }

      const formattedHistory = rawHistory
        .map((item) => {
          const dateSource = item.authorized_date || item.date;
          const dateObj = new Date(dateSource);

          return {
            // Keep the timestamp for accurate chronological sorting
            timestamp: dateObj.getTime(),
            // Label: "Nov", "Dec", etc.
            // Optional: Use `${dateObj.toLocaleString("default", { month: "short" })} ${dateObj.getFullYear().toString().slice(-2)}` for "Nov 25"
            label: `${dateObj.toLocaleString("default", {
              month: "short",
            })}/${dateObj.getFullYear()}`,
            value: Math.abs(parseFloat(item.amount)),
          };
        })
        // Sort by timestamp: Oldest (Left) to Newest (Right)
        .sort((a, b) => a.timestamp - b.timestamp)
        // Take the last 7 months of history
        .slice(-7);

      setHistoryData(formattedHistory);
    } catch (error) {
      console.error("Error fetching transaction history:", error);
    } finally {
      setIsLoadingHistory(false);
    }
  }, [transaction?.recurring_uuid]);
  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handleRecurrentTypeChange = async (selectedTypeCode) => {
    const detectedTypeCode =
      currentTransaction.detected_recurrent_type_code ||
      currentTransaction.recurrent_type_code;
    const nextOverride =
      selectedTypeCode === detectedTypeCode ? null : selectedTypeCode;
    const previousTransaction = currentTransaction;

    if (nextOverride === (currentTransaction.recurrent_type_override || null)) {
      setIsRecurrentTypeMenuOpen(false);
      return;
    }

    setUpdateError("");
    setIsSavingRecurrentType(true);
    setIsRecurrentTypeMenuOpen(false);
    setCurrentTransaction((prevTransaction) => {
      const optimisticTypeCode =
        nextOverride ||
        prevTransaction.detected_recurrent_type_code ||
        prevTransaction.recurrent_type_code;

      return {
        ...prevTransaction,
        recurrent_type_override: nextOverride,
        recurrent_type_code: optimisticTypeCode,
        recurrent_type:
          RECURRENT_TYPE_LABELS[optimisticTypeCode] || commonCopy?.other,
      };
    });

    try {
      const updatedTransaction = await updateRecurringTransaction(
        currentTransaction.recurring_uuid || currentTransaction.uuid,
        {
          recurrent_type_override: nextOverride,
        },
      );
      const mappedTransaction =
        mapRecurringTransactionToRow(updatedTransaction);
      setCurrentTransaction(mappedTransaction);
      if (onTransactionUpdate) {
        onTransactionUpdate(mappedTransaction);
      }
      showToast(copy?.recurring_type_saved);
    } catch (error) {
      setCurrentTransaction(previousTransaction);
      setUpdateError(error.message || copy?.unable_to_save_recurring_type);
    } finally {
      setIsSavingRecurrentType(false);
    }
  };

  const handleSaveNote = async () => {
    setUpdateError("");
    setIsSavingNote(true);

    try {
      const updatedTransaction = await updateRecurringTransaction(
        currentTransaction.recurring_uuid || currentTransaction.uuid,
        {
          note: noteValue,
        },
      );
      const mappedTransaction =
        mapRecurringTransactionToRow(updatedTransaction);
      setCurrentTransaction(mappedTransaction);
      setNoteValue(mappedTransaction.note || "");
      if (onTransactionUpdate) {
        onTransactionUpdate(mappedTransaction);
      }
      showToast(commonCopy?.note_saved);
    } catch (error) {
      setUpdateError(error.message || copy?.unable_to_save_note);
    } finally {
      setIsSavingNote(false);
    }
  };

  if (!currentTransaction) return null;

  const flowClass =
    currentTransaction.flow === "spending"
      ? "type-bill"
      : "type-pill type-subscription";
  const category = currentTransaction.category || commonCopy?.uncategorized;
  const categoryCode =
    currentTransaction.category_code || currentTransaction.category_value;
  const recurrentTypeOptions = RECURRENT_TYPE_OPTIONS.filter((option) =>
    option.flows.includes(currentTransaction.flow),
  );
  const selectedRecurrentTypeLabel =
    currentTransaction.recurrent_type || commonCopy?.other;
  const hasNoteChanges = noteValue !== (currentTransaction.note || "");

  return (
    <>
      <ModalModern onClose={onClose}>
        <ModernPopupHeader onClose={onClose}>
          <div className="header-content-modern-row">
            <h3 className="transaction-popup-title">
              {currentTransaction.institution} | {currentTransaction.account} |
              ••{currentTransaction.last_four_digits}
            </h3>
          </div>
        </ModernPopupHeader>
        <ModernPopupBody>
          <div className="mp-col-container">
            <div className="mp-row-container mobile-to-col padding-reset">
              <div className="mp-transacion-metadata">
                <div
                  className="mp-col-container"
                  style={{
                    gap: "24px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "12px",
                    }}
                  >
                    <span
                      className={flowClass}
                      style={{ maxWidth: "120px", textAlign: "center" }}
                    >
                      {currentTransaction.flow.charAt(0).toUpperCase() +
                        currentTransaction.flow.slice(1)}
                    </span>
                    <div className="mp-transaction-merchant-status">
                      <div className="mp-merchant-icon">
                        <img
                          src={
                            currentTransaction.merchant_icon
                              ? getIconSrc(currentTransaction.merchant_icon)
                              : getInitialsIconSrc(currentTransaction.merchant)
                          }
                          alt={currentTransaction.merchant}
                        />
                      </div>
                      <div className="mp-merchant-name-status">
                        <div>
                          <p>{currentTransaction.merchant}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mp-transaction-amount-category">
                    <div>
                      <span className="mp-transaction-amount">
                        {currentTransaction.amount}
                      </span>
                    </div>
                    <div>
                      <CategoryPill
                        label={category}
                        categoryCode={categoryCode}
                        categoryPrimary={currentTransaction.category_primary}
                        categoryDetailed={currentTransaction.category_detailed}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="mp-transacion-actions">
                <p>{commonCopy?.actions}</p>
                <div
                  className="mp-recurring-type-field"
                  ref={recurrentTypeFieldRef}
                >
                  <button
                    type="button"
                    className="mp-recurring-type-trigger"
                    onClick={() =>
                      setIsRecurrentTypeMenuOpen((isOpen) => !isOpen)
                    }
                    disabled={isSavingRecurrentType}
                    aria-label={copy?.recurrent_type_aria?.replace(
                      "{type}",
                      selectedRecurrentTypeLabel,
                    )}
                    aria-haspopup="listbox"
                    aria-expanded={isRecurrentTypeMenuOpen}
                  >
                    <span
                      className={getRecurrentTypeClassName(
                        selectedRecurrentTypeLabel,
                      )}
                    >
                      {selectedRecurrentTypeLabel}
                    </span>
                  </button>
                  {isRecurrentTypeMenuOpen && (
                    <div className="mp-recurring-type-menu" role="listbox">
                      <button
                        type="button"
                        className="mp-recurring-type-menu-close"
                        onClick={() => setIsRecurrentTypeMenuOpen(false)}
                        aria-label={copy?.close_recurrent_type_menu}
                      >
                        ×
                      </button>
                      {recurrentTypeOptions.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          className="mp-recurring-type-option"
                          onClick={() =>
                            handleRecurrentTypeChange(option.value)
                          }
                          role="option"
                          aria-selected={
                            option.value ===
                            currentTransaction.recurrent_type_code
                          }
                        >
                          <span
                            className={getRecurrentTypeClassName(option.label)}
                          >
                            {option.label}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {/*
              Tax deductible for recurring transactions is hidden until the
              product behavior is defined.
              <div className="mp-transaction-tax-deductible">
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                  }}
                >
                  <img
                    src={`${process.env.PUBLIC_URL}/images/icons/calculator.svg`}
                    alt="Calculator Icon"
                  />
                  <span>Tax Deductible?</span>
                </div>
                <div className="switch-container">
                  <input
                    type="checkbox"
                    className="edit-switch"
                    defaultChecked={transaction.tax_deductible}
                  />
                  <label className="switch-label">
                    <span className="switch-slider"></span>
                  </label>
                </div>
              </div>
              */}
                <div className="mp-transaction-notes">
                  <textarea
                    placeholder={commonCopy?.add_note}
                    value={noteValue}
                    onChange={(event) => setNoteValue(event.target.value)}
                    disabled={isSavingNote}
                  ></textarea>
                  {updateError && (
                    <ErrorLabel size="sm">{updateError}</ErrorLabel>
                  )}
                  {hasNoteChanges && (
                    <div
                      style={{ display: "flex", justifyContent: "flex-end" }}
                    >
                      <button
                        type="button"
                        className="cta-button primary rounded small"
                        disabled={isSavingNote}
                        onClick={handleSaveNote}
                        style={{ marginTop: "12px" }}
                      >
                        {isSavingNote
                          ? commonCopy?.saving
                          : commonCopy?.save_note}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="mp-col-container">
              <div className="mp-rec-transaction-history">
                <p>{copy?.payment_history}</p>
                {isLoadingHistory ? (
                  <div style={{ marginTop: "12px" }}>
                    <LoadingPlaceholder rows={1} type="card-large" />
                  </div>
                ) : (
                  <TransactionHistoryChart data={historyData} />
                )}
              </div>
            </div>
          </div>
        </ModernPopupBody>
      </ModalModern>
      {toastMessage && (
        <div
          role="status"
          aria-live="polite"
          className="app-toast app-toast-primary"
        >
          {toastMessage}
        </div>
      )}
    </>
  );
}

export default RecurringDetailsModal;
